import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import SEO, { orgJsonLd, websiteJsonLd } from '../components/SEO';
import Img from '../components/Img';
import { EDITORIAL_HEROES } from '../data/heroImages';

// D-082 · Founder Visual Replication — exact 3-band editorial home (01 HOME · 02 PRODUCTS · 03 FACTORIES).
// Each band: left copy column · centre editorial image · right gold-bordered 4-cell stat grid.

const STATS = [
  { value: '1,240+', label: 'Verified Factories' },
  { value: '12',     label: 'Sourcing Countries' },
  { value: '82',     label: 'Certifications Indexed' },
  { value: '8',      label: 'Supplier Types' },
];

const PhilosophyStrip = () => (
  <div className="mt-8 flex items-center gap-3 font-mono-data text-[11px] tracking-[0.18em] uppercase" style={{ color: '#C8A96B' }}>
    <span>Trust</span>
    <span style={{ color: 'rgba(200,169,107,0.45)' }}>·</span>
    <span>Connect</span>
    <span style={{ color: 'rgba(200,169,107,0.45)' }}>·</span>
    <span>Trade</span>
    <span style={{ color: 'rgba(200,169,107,0.45)' }}>·</span>
    <span>Grow</span>
  </div>
);

const StatGrid = ({ testId }) => (
  <div className="editorial-stat-grid" data-testid={testId}>
    {STATS.map((s) => (
      <div key={s.label}>
        <div className="stat-value">{s.value}</div>
        <div className="stat-label">{s.label}</div>
      </div>
    ))}
  </div>
);

const EditorialBand = ({ number, label, headline, headlineGold, body, ctas, image, alt, statsTestId, bandTestId }) => (
  <section className="editorial-band" data-testid={bandTestId}>
    <div className="editorial-band__copy">
      <div className="flex items-baseline gap-5">
        <span className="font-mono-data text-sm tracking-[0.16em]" style={{ color: 'rgba(200,169,107,0.55)' }}>{number}</span>
        <span className="font-mono-data text-xs tracking-[0.24em] uppercase text-platinum-2">{label}</span>
      </div>
      <h1 className="mt-6 font-display tracking-tighter text-white" style={{ fontWeight: 600, fontSize: 'clamp(2.4rem, 4.4vw, 4.5rem)', lineHeight: 0.96 }}>
        {headline}<br />
        <em
          className="giwahs-gold-emphasis"
          style={{ fontStyle: 'italic' }}
        >{headlineGold}</em>
      </h1>
      <p className="mt-6 max-w-md text-sm leading-relaxed text-platinum-2 md:text-base">
        {body}
      </p>
      <div className="mt-8 flex flex-wrap items-center gap-3">
        {ctas.map((c, i) => (
          <Link key={i} to={c.to} data-testid={c.testId} className="btn-giwahs btn-giwahs-outline-gold">
            {c.label} <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        ))}
      </div>
      <PhilosophyStrip />
    </div>

    <div className="editorial-band__image">
      <Img src={image} alt={alt} variant="hero" priority className="h-full w-full object-cover" />
    </div>

    <div className="editorial-band__stats">
      <StatGrid testId={statsTestId} />
    </div>
  </section>
);

const Home = () => {
  return (
    <div data-testid="home-page" className="surface-obsidian">
      <SEO
        title="Global Sourcing Intelligence Platform"
        description="GIWAHS is the editorial intelligence layer for global fashion sourcing. Discover trusted manufacturers, unlock global intelligence, and build products the world will remember."
        keywords="sourcing intelligence, factory directory, fashion manufacturing, OEM, ODM, streetwear suppliers, activewear factories"
        jsonLd={[orgJsonLd, websiteJsonLd]}
      />

      {/* 01 — HOME */}
      <EditorialBand
        bandTestId="home-band-01"
        number="01"
        label="Home"
        headline="Source with"
        headlineGold="Confidence."
        body="Discover trusted manufacturers, unlock global intelligence, and build products the world will remember."
        image={EDITORIAL_HEROES.home.src}
        alt={EDITORIAL_HEROES.home.alt}
        statsTestId="home-band-01-stats"
        ctas={[
          { label: 'Explore GIWAHS', to: '/factories', testId: 'hero-explore-giwahs-cta' },
          { label: 'Become a Founding Partner', to: '/founding-partners', testId: 'hero-become-founder-cta' },
        ]}
      />

      {/* 02 — PRODUCTS */}
      <EditorialBand
        bandTestId="home-band-02"
        number="02"
        label="Products"
        headline="Browse the"
        headlineGold="Catalogue."
        body="Explore a curated range of premium products sourced from the world's most trusted manufacturers."
        image={EDITORIAL_HEROES.products.src}
        alt={EDITORIAL_HEROES.products.alt}
        statsTestId="home-band-02-stats"
        ctas={[
          { label: 'Browse Products', to: '/products', testId: 'products-band-browse-cta' },
          { label: 'Request Product Sourcing', to: '/rfq', testId: 'products-band-rfq-cta' },
        ]}
      />

      {/* 03 — FACTORIES */}
      <EditorialBand
        bandTestId="home-band-03"
        number="03"
        label="Factories"
        headline="Verified Factories"
        headlineGold="Worldwide."
        body="Connect with verified factories in 12+ sourcing countries. Built on trust. Backed by intelligence."
        image={EDITORIAL_HEROES.factories.src}
        alt={EDITORIAL_HEROES.factories.alt}
        statsTestId="home-band-03-stats"
        ctas={[
          { label: 'Explore Factories', to: '/factories', testId: 'factories-band-explore-cta' },
          { label: 'Join as a Factory', to: '/for-suppliers', testId: 'factories-band-join-cta' },
        ]}
      />
    </div>
  );
};

export default Home;
